import { service } from '@ember/service'
import Component from '@glimmer/component'
import { AlertType, InlineAlert } from 'teacher-ui-client/components/inline-alert'
import type StandardsSet from 'teacher-ui-client/models/standards-set'
import type ReportingService from 'teacher-ui-client/services/reporting'
import type SessionService from 'teacher-ui-client/services/session'
import { t, type IntlService } from 'ember-intl'

export const NON_MX_STANDARDS_SETS_NAMES: readonly string[] = [
  'ACT (AC V9.0)',
  'Alabama Course of Study',
  'Alberta Program of Studies',
  'Arizona Standards',
  'Arkansas Academic Standards',
  'Australian Curriculum V9.0',
  'British Columbia Learning Standards',
  'California Content Standards',
  'Colorado Academic Standards',
  'Common Core',
  'Connecticut Core Standards',
  'England NC',
  "Florida's Benchmarks for Excellent Student Thinking",
  'Georgia Standards of Excellence',
  'Idaho Content Standards',
  'Illinois Learning Standards',
  'Indiana Academic Standards',
  'Iowa Academic Standards',
  'Ireland PLC / MA',
  'Kansas Curricular Standards',
  'Kentucky Academic Standards',
  'Louisiana Student Standards',
  'Manitoba Curriculum',
  'Maryland State Standards',
  'Michigan Academic Standards',
  'Minnesota Academic Standards',
  'Mississippi College- and Career-Readiness Standards',
  'Missouri Learning Standards',
  'NSW Syllabus',
  'NT (AC V9.0)',
  'New Brunswick Curriculum',
  'New Jersey Student Learning Standards',
  'New York State Next Generation Learning Standards',
  'New Zealand',
  'Newfoundland and Labrador Curriculum',
  'North Carolina Standard Course of Study',
  'Northern Ireland NIC',
  'Nova Scotia Curriculum',
  "Ohio's Learning Standards",
  'Oklahoma Academic Standards',
  'Ontario Curriculum',
  'Oregon Academic Standards',
  'Pennsylvania Core Standards',
  'Prince Edward Island Curriculum',
  'QLD ACiQ',
  'Quebec Progression of Learning',
  'Republic of South Africa CAPS',
  'SA (AC V9.0)',
  'Saskatchewan Curriculum',
  'Scotland CfE',
  'South Carolina College- and Career-Ready Standards',
  'Tasmania (AC V9.0)',
  'Tennessee Academic Standards',
  'Texas Essential Knowledge and Skills',
  'Victoria 2.0',
  'Virginia Standards of Learning',
  'WA Curriculum V8.1',
  'Wales CfW',
  'Washington State Learning Standards',
  'West Virginia Standards',
  'Wisconsin Academic Standards',
] as const

export function isSupportedStandardsSet(standardsSet: StandardsSet): boolean {
  return NON_MX_STANDARDS_SETS_NAMES.includes(standardsSet.name)
}

export function isUnsupportedStandardsSet(standardsSet: StandardsSet): boolean {
  return !isSupportedStandardsSet(standardsSet)
}

/**
 * Component to display an alert when the current standards set is unsupported for the given product.
 * Conditionally render based on the result of helper `(currentStandardsSetUnsupported)`.
 */
export class StandardsSetUnsupportedAlert extends Component<unknown> {
  @service reporting!: ReportingService
  @service session!: SessionService
  @service intl!: IntlService

  get currentStandardsSetName(): string | undefined {
    return this.session.currentSchool.standardsSet?.name
  }

  get productName() {
    const product = this.reporting.product
    return this.intl.t(`products.${product}`)
  }

  <template>
    <InlineAlert data-test-report-unsupported-standards-alert @type={{AlertType.Warning}}>
      <div class="space-y-2">
        <h2 class="m-0 text-sm font-medium">{{t "reporting.alerts.standardsAlert.title"}}</h2>
        <p class="m-0 text-sm">
          <span>{{t
              "reporting.alerts.standardsAlert.currentStandards"
              htmlSafe=true
              name=this.currentStandardsSetName
            }}</span>
          <div>{{t
              "reporting.alerts.standardsAlert.unsupportedStandardsSet"
              htmlSafe=true
              productName=this.productName
            }}</div>
        </p>
      </div>
    </InlineAlert>
  </template>
}
